import { useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from 'modules/auth/context/auth.consumer';
import CustomizedTable from './components/Table';
import { BaseCardBox, Header, BaseTextField, FieldRow } from './style';
import { FileApi } from 'lib/api/file';
import { FileDto, ReportType } from 'lib/api/file.protocols';
import { Stack, Pagination } from '@mui/material';
import { useSnackbar } from 'notistack';
import { UserApi } from 'lib/api/user';
import SectorFilter from 'lib/components/SectorFilter';
import { IElement } from 'lib/common/types';

interface ITableBody {
  fileName: string;
  sectors: IElement[];
  actions: JSX.Element;
}

const AnalystReports = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState<FileDto[]>([]);
  const [search, setSearch] = useState('');
  const [tableBody, setTableBody] = useState<ITableBody[]>([]);
  const tableHead = ['Nome do arquivo', 'Setores', 'Ações'];
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState<number | undefined>(undefined);
  const [userSectors, setUserSectors] = useState<IElement[]>([]);
  const [selectedSectorIds, setSelectedSectorIds] = useState<string[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  const getUserSectors = async () => {
    if (!user) return;
    const userFromDb = await UserApi.getUser(user.id);
    setUserSectors(userFromDb.sectors || []);
  };

  const getRequestsPaginated = async () => {
    const response = await FileApi.searchByCompanyAndSectors(
      page,
      search,
      selectedSectorIds.length ? selectedSectorIds : undefined,
    );
    setReports(response.entities);
    setPageCount(response.meta.pageCount);
  };

  useEffect(() => {
    if (user) {
      getUserSectors();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      getRequestsPaginated();
    }
  }, [user, search, page, selectedSectorIds]);

  const getReportUrl = async (
    azureFileName: string,
    reportUser: IElement | undefined,
  ) => {
    try {
      const reportType = reportUser
        ? ReportType.CONSULTANT_REPORT
        : ReportType.ASSESSMENT_REPORT;

      const url = await FileApi.getReportUrl({ azureFileName, reportType });
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', url);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('Erro ao gerar url do arquivo', { variant: 'error' });
    }
  };

  useEffect(() => {
    const newTableBody =
      reports?.map((item) => ({
        fileName: item.originalFileName || item.fileName,
        sectors: item.sectors,
        actions: (
          <div
            className="actions"
            onClick={() => getReportUrl(item.azureFileName, item.user)}
          >
            <DownloadIcon />
          </div>
        ),
      })) || [];

    setTableBody(newTableBody);
  }, [reports]);

  return (
    <Container>
      <BaseCardBox>
        <Header>
          <h3>Relatórios</h3>
        </Header>

        <FieldRow>
          <BaseTextField
            onChange={(event) => {
              page !== 1 && setPage(1);
              setSearch(event.target.value);
            }}
            label="Realizar busca"
            variant="outlined"
          />

          <SectorFilter
            sectors={userSectors}
            selectedIds={selectedSectorIds}
            onChange={(ids) => {
              page !== 1 && setPage(1);
              setSelectedSectorIds(ids);
            }}
            label="Setor"
          />
        </FieldRow>
      </BaseCardBox>

      <CustomizedTable tableHead={tableHead} tableBody={tableBody} />
      <Stack spacing={2} alignItems={'center'}>
        <Pagination
          count={pageCount}
          page={page}
          onChange={(e, value) => setPage(value)}
          defaultPage={page}
        />
      </Stack>
    </Container>
  );
};

export { AnalystReports };
